import { Children, type ReactNode, useState } from "react";
import ReactMarkdown from "react-markdown";
import { CircularProgress } from "@mui/material";
import remarkGfm from "remark-gfm";
import AiAssistantExpandableTree from "../AiAssistantExpandableTree/AiAssistantExpandableTree";
import { markdownComponents } from "../MessageBubble/MessageBubble.markdownComponents";
import { TOOL_CALL_INDICATOR_NODE_TITLE_CLASS } from "../ToolCallIndicators/ToolCallIndicator.constants";
import {
  type SubagentIndicatorProps,
  SubagentStatus,
} from "./SubagentIndicator.types";

const remarkPlugins = [remarkGfm];

export default function SubagentIndicator({
  subagentType,
  description,
  status,
  activity,
  isError,
  children,
  "data-test-id": testId,
}: SubagentIndicatorProps) {
  const isRunning = status === SubagentStatus.Pending;
  // Open while the subagent runs so its work streams in view, closed once it is
  // done; a click by the user wins over both.
  const [userExpanded, setUserExpanded] = useState<boolean>();
  const expanded = userExpanded ?? isRunning;

  let title: ReactNode = (
    <>
      Subagent ·{" "}
      <span className={TOOL_CALL_INDICATOR_NODE_TITLE_CLASS}>
        {subagentType ?? "unknown"}
      </span>
      {isRunning && activity && (
        <span className="text-text-secondary"> · {activity}…</span>
      )}
      {isError && (
        <span className="text-error-dark dark:text-error-light"> · Error</span>
      )}
    </>
  );

  if (isRunning) {
    title = (
      <div>
        <CircularProgress size={10} className="mr-1 leading-0" /> {title}
      </div>
    );
  }

  return (
    // Framed so the subagent's transcript visibly ends before the main thread continues.
    <AiAssistantExpandableTree
      title={title}
      expanded={expanded}
      onExpandedChange={setUserExpanded}
      framed
      data-test-id={testId}
    >
      {description && (
        <AiAssistantExpandableTree.Item branchYOffset="1.3em">
          <div className="text-text-secondary whitespace-pre-wrap">
            {description}
          </div>
        </AiAssistantExpandableTree.Item>
      )}
      {children && (
        <AiAssistantExpandableTree.Item branchYOffset="1.3em">
          <div className="flex flex-col gap-1 whitespace-pre-wrap">
            {/* Text items arrive as strings; render them as markdown like the main bubble. */}
            {Children.map(children, (child) =>
              typeof child === "string" ? (
                <ReactMarkdown
                  remarkPlugins={remarkPlugins}
                  components={markdownComponents}
                >
                  {child}
                </ReactMarkdown>
              ) : (
                child
              )
            )}
          </div>
        </AiAssistantExpandableTree.Item>
      )}
    </AiAssistantExpandableTree>
  );
}
